<template>
  <div class="view" v-cloak>
    <GridPanelHeader
      :title="order.order_number + ' / ' + order.product_name"
      :grid-ref="$refs[dtGridRefKey]"
      :show-add="false"
      :show-filter="false"
      :show-export="false"
      :show-columns="false"
    />
    <DxDataGrid
      :ref="dtGridRefKey"
      :data-source="ordersDetailDataSource"
      :remote-operations="gridRemoteOperations"
      :column-min-width="80"
      style="min-height: 350px"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :showBorders="true"
      :column-auto-width="false"
      :allowColumnReordering="true"
      :allowColumnResizing="true"
      :row-alternation-enabled="true"
      :searchPanel="{ visible: false, width: 240, placeholder: 'Search...' }"
      :element-attr="gridAttributes"
      @exporting="onExportingDetails"
      :row-height="50"
    >
      <DxColumn
        dataField="reception_number"
        :caption="$t('common.reception_number')"
        :allowHeaderFiltering="false"
        cell-template="cellTemplatePdfR"
      ></DxColumn>
      <template #cellTemplatePdfR="{ data }">
        <div>
          <img
            v-if="!!data.data.reception_number"
            src="~@/assets/images/desktop/icons/controls/pdf.svg"
            class="icon-svg danger-filter"
            @click="handleDownloadPdf(data.data.download_url, data.data.download_filename)"
            :title="
              $t('common.download_document_pdf', {
                document: data.data.reception_number,
              })
            "
          />
          {{ data.data.reception_number }}
        </div>
      </template>
      <DxColumn
        dataField="sale_number"
        :caption="$t('common.sale_number')"
        :allowHeaderFiltering="false"
        cell-template="cellTemplatePdfS"
      ></DxColumn>
      <template #cellTemplatePdfS="{ data }">
        <div>
          <img
            v-if="!!data.data.sale_number"
            src="~@/assets/images/desktop/icons/controls/pdf.svg"
            class="icon-svg danger-filter"
            @click="handleDownloadPdf(data.data.download_url, data.data.download_filename)"
            :title="
              $t('common.download_document_pdf', {
                document: data.data.sale_number,
              })
            "
          />
          {{ data.data.sale_number }}
        </div>
      </template>
      <DxColumn
        dataField="production_report_number"
        :caption="$t('common.production_report_number')"
        cell-template="cellTemplatePdfRp"
        :allowHeaderFiltering="false"
        :visible="false"
      >
      </DxColumn>
      <template #cellTemplatePdfRp="{ data }">
        <div>
          <img
            v-if="!!data.data.production_report_number"
            src="~@/assets/images/desktop/icons/controls/pdf.svg"
            class="icon-svg danger-filter"
            @click="handleDownloadPdf(data.data.download_url, data.data.download_filename)"
            :title="
              $t('common.download_document_pdf', {
                document: data.data.production_report_number,
              })
            "
          />
          {{ data.data.production_report_number }}
        </div>
      </template>
      <DxColumn
        dataField="delivery_note_number"
        :caption="$t('common.delivery_note_number')"
        :allowHeaderFiltering="false"
        cell-template="cellTemplatePdfDN"
        :visible="false"
      ></DxColumn>
      <template #cellTemplatePdfDN="{ data }">
        <div>
          <img
            v-if="!!data.data.delivery_note_number"
            src="~@/assets/images/desktop/icons/controls/pdf.svg"
            class="icon-svg danger-filter"
            @click="handleDownloadPdf(data.data.field_delivery_note_download_url, data.data.field_delivery_note_download_filename)"
            :title="
              $t('common.download_document_pdf', {
                document: data.data.delivery_note_number,
              })
            "
          />
          {{ data.data.delivery_note_number }}
        </div>
      </template>
      <DxColumn
        dataField="weighing_number"
        :caption="$t('common.weighing_number')"
        :allowHeaderFiltering="false"
        cell-template="cellTemplatePdfWOS"
        :visible="false"
      ></DxColumn>
      <template #cellTemplatePdfWOS="{ data }">
        <div>
          <img
            v-if="!!data.data.weighing_number"
            src="~@/assets/images/desktop/icons/controls/pdf.svg"
            class="icon-svg danger-filter"
            @click="handleDownloadPdf(data.data.weighing_download_url, data.data.weighing_download_filename)"
            :title="
              $t('common.download_document_pdf', {
                document: data.data.weighing_number,
              })
            "
          />
          {{ data.data.weighing_number }}
        </div>
      </template>
      <DxColumn
        dataField="transfer_number"
        :caption="$t('stock_management.orders.transfer_number')"
        :allowHeaderFiltering="false"
        cell-template="cellTemplatePdfTransfer"
      ></DxColumn>
      <template #cellTemplatePdfTransfer="{ data }">
        <div>
          <img
            v-if="!!data.data.transfer_number"
            src="~@/assets/images/desktop/icons/controls/pdf.svg"
            class="icon-svg danger-filter"
            @click="handleDownloadPdf(data.data.download_url, data.data.download_filename)"
            :title="
              $t('common.download_document_pdf', {
                document: data.data.transfer_number,
              })
            "
          />
          {{ data.data.transfer_number }}
        </div>
      </template>
      <DxColumn
        dataField="date"
        :caption="$t('common.date')"
        :sortOrder="'desc'"
        :allowHeaderFiltering="false"
        dataType="date"
        :allowEditing="false"
        :format="getDxGridDefaultDateFormat"
        :width="130"
      >
      </DxColumn>
      <DxColumn
        dataField="quantity"
        :caption="$t('common.quantity')"
        :dataType="'number'"
        :allowHeaderFiltering="false"
      ></DxColumn>
      <DxColumn dataField="measure_unit" :caption="$t('common.measure_unit')" :allowHeaderFiltering="false">
      </DxColumn>
      <DxColumn dataField="notes" :caption="$t('common.notes')" :allowHeaderFiltering="false"> </DxColumn>
      <DxPaging :total="3500" :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxHeaderFilter :visible="true" />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
      <DxSearchPanel :visible="true" :width="240" />
      <DxExport :enabled="true" :allow-export-selected-data="false" :fileName="'SmOrdersDetail'" />

      <DxSummary>
        <DxTotalItem column="quantity" summaryType="sum" valueFormat="#,##0.##" displayFormat="Total: {0}" />
        <DxGroupItem column="quantity" summaryType="sum" valueFormat="#,##0.##" displayFormat="Group Total: {0}" />
      </DxSummary>
    </DxDataGrid>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxTexts,
  DxButton,
  DxPopup,
  DxItem,
  DxSearchPanel,
  DxExport,
  DxLookup,
  DxSummary,
  DxTotalItem,
  DxGroupItem,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import { permissions, permissionTypes } from "~/helpers/Permissions";
import { format } from "date-fns";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import notify from "devextreme/ui/notify";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";

export default {
  name: "OrdersDetailList",
  components: {
    DxDataGrid,
    DxColumn,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxTexts,
    DxButton,
    DxPaging,
    DxPager,
    DxPopup,
    DxItem,
    DxSearchPanel,
    DxExport,
    DxLookup,
    DxSummary,
    DxTotalItem,
    DxGroupItem,
    GridPanelHeader,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid orders-detail-grid",
      },
      dtGridRefKey: "ordersDetailGrid",
      ordersDetailDataSource: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = {
            data: [],
            totalCount: 0,
          };

          let pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
          let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

          let payload = {
            page: pageNumber,
            limit: pageLimit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,
            order_uuid: this.order.uuid,
          };

          try {
            await this.getSmOrderDetails(payload).then((response) => {
              dataSource.data = response.data;
            });

            return dataSource;
          } catch (error) {
            console.error("Error fetching orders details:", error);
            throw error;
          }
        },
      }),
      gridRemoteOperations: {
        paging: false,
        filtering: false,
        sorting: false,
      },
      pageSizes: [15, 25, 100],
      yes_no_list: [
        {
          id: 1,
          label: this.$t("common.yes"),
        },
        {
          id: 0,
          label: this.$t("common.no"),
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    config() {
      return {
        permission: permissions.SM_ORDERS,
        permission_type: permissionTypes.access,
      };
    },
    dataGridInstance: function () {
      const grid = this.$refs && this.$refs[this.dtGridRefKey] ? this.$refs[this.dtGridRefKey] : null;
      return grid && grid.instance ? grid.instance : null;
    },
  },
  methods: {
    ...mapActions({
      getSmOrderDetails: "smOrders/getSmOrderDetails",
      downloadPdf: "scale/downloadPdf",
    }),
    onExportingDetails(e) {
      e.fileName = "SmOrdersDetail_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");

      if (e.format === "xlsx") {
        const fields = ["deleted_at"];
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("SmOrdersDetail");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            if (fields.includes(gridCell?.column?.dataField) && excelCell.row > 1) {
              excelCell.value = this.getYesNoColumnText(!!gridCell.data[gridCell?.column?.dataField]);
            }
          },
        }).then(() => {
          worksheet.insertRow(1, [this.$t("stock_management.orders.order_number") + ":  " + this.order.order_number]);
          worksheet.mergeCells(1, 1, 1, 2);

          worksheet.insertRow(2, [this.$t("common.partner") + ":  " + this.order.partner_name]);
          worksheet.mergeCells(2, 1, 2, 2);

          worksheet.insertRow(3, [this.$t("common.product") + ":  " + this.order.product_name]);
          worksheet.mergeCells(3, 1, 3, 2);

          worksheet.insertRow(4, [""]);

          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    notifyMsg(response) {
      notify({
        message: response.message ?? "",
        type: response.status == "ok" ? "success" : "error",
        displayTime: 2500,
        width: "auto",
        position: { at: "top center", my: "top center" },
      });
    },
    getYesNoColumnText(value) {
      if (value === null) {
        return "";
      }
      return this.yes_no_list.find((item) => item.id == value)?.label;
    },
    handleDownloadPdf(download_url, download_filename) {
      this.downloadPdf({
        url: download_url,
        filename: download_filename,
        response_type: "blob",
      }).catch((e) => {
        console.error("Error downloading file:", e);
      });
    },
  },
  mounted() {},
};
</script>
